import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DayPicker from 'react-day-picker';
import moment from 'moment';
import { Icon } from '@cbrebuild/blocks';
import 'react-day-picker/lib/style.css';

/* Legacy comments:
  The React-Day-Picker package deals with native JS date objects. This component
  acts as a shim to interface with the picker in a way that makes it easy to use.
  We use moment to mutate JS date objects to date strings that our frontend
  can display in the format: 'M/D/YYYY'

  This shim takes in 2 required props:
  1. onDateChange() required prop that updates the parent when a date change occurss
  2. 'date' required prop that sets the date from parent
  3. optional updateValidity() prop so this component can inform its parents when an invalid date has been entered

  Our backend consumes dates in the format: 'YYYY-MM-DD' to be search query friendly, any dates
  coming in as props should have this format to minimize date format
  manipulation oustide of this component. We need to ensure that dates leaving this component
  are formatted properly for backend consumption
*/

const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CURRENT_YEAR = new Date().getFullYear();
// 10 years back, 50 years forward — covers historical data and long-term leases
const YEAR_RANGE = Array.from({ length: 61 }, (_, i) => (CURRENT_YEAR - 10) + i);

// Year/month scroll grid — replicates Angular Material md-datepicker with md-current-view="year"
const YearMonthPicker = ({ selectedDate, onMonthSelect, anchorYear }) => {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const ref = useRef(null);

  const getMonthButtonClass = (year, month) => {
    const isSelected = selectedDate
      && selectedDate.getFullYear() === year
      && selectedDate.getMonth() === month;

    if (isSelected) return ' nd-year-month-picker__month--selected';
    if (todayYear === year && todayMonth === month) return ' nd-year-month-picker__month--default';
    return '';
  };

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current.querySelector(`[data-year="${anchorYear}"]`);
    if (el) {
      // Keep the anchor year at the top so opening starts on the expected year
      ref.current.scrollTop = el.offsetTop;
    }
  }, [anchorYear]);

  return (
    <div className="nd-year-month-picker" ref={ref}>
      {YEAR_RANGE.map(year => (
        <div key={year} className="nd-year-month-picker__group" data-year={year}>
          <div className="nd-year-month-picker__row">
            <span className="nd-year-month-picker__year">{year}</span>
            {MONTHS_ABBR.slice(0, 6).map((m, i) => (
              <button
                key={m}
                type="button"
                className={`nd-year-month-picker__month${getMonthButtonClass(year, i)}`}
                onClick={() => onMonthSelect(year, i)}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="nd-year-month-picker__row">
            <span className="nd-year-month-picker__year-spacer" />
            {MONTHS_ABBR.slice(6).map((m, i) => (
              <button
                key={m}
                type="button"
                className={`nd-year-month-picker__month${getMonthButtonClass(year, i + 6)}`}
                onClick={() => onMonthSelect(year, i + 6)}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

YearMonthPicker.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  onMonthSelect: PropTypes.func.isRequired,
  anchorYear: PropTypes.number.isRequired,
};

YearMonthPicker.defaultProps = {
  selectedDate: null,
};

// Weekday labels for the sticky header in the scrollable day picker
const WEEKDAY_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
// 6 months before + current + 18 months after = 25 months of scrollable days
const SCROLL_MONTHS_BEFORE = 6;
const SCROLL_MONTHS_AFTER = 18;
const TOTAL_SCROLL_MONTHS = SCROLL_MONTHS_BEFORE + SCROLL_MONTHS_AFTER + 1;

// Scrollable multi-month day picker — sticky weekday header, months stack vertically
const ScrollableMonthPicker = ({
  selectedDate,
  anchorMonth,
  onDayClick,
  onBackToYear,
}) => {
  const today = new Date();
  const scrollRef = useRef(null);
  const firstMonth = new Date(anchorMonth.getFullYear(), anchorMonth.getMonth() - SCROLL_MONTHS_BEFORE, 1);
  const anchorKey = `${anchorMonth.getFullYear()}-${anchorMonth.getMonth()}`;
  const scrollMonths = Array.from(
    { length: TOTAL_SCROLL_MONTHS },
    (_, i) => new Date(firstMonth.getFullYear(), firstMonth.getMonth() + i, 1),
  );
  const isSelectedToday = selectedDate
    && selectedDate.getFullYear() === today.getFullYear()
    && selectedDate.getMonth() === today.getMonth()
    && selectedDate.getDate() === today.getDate();

  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-month-key="${anchorKey}"]`);
    if (el) {
      // Use scrollTop directly to avoid scrollIntoView() scrolling the whole page
      const containerTop = scrollRef.current.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      scrollRef.current.scrollTop += elTop - containerTop;
    }
  }, [anchorKey]);

  const handleBackToYear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onBackToYear();
  };

  return (
    <div className="nd-day-scroll-picker">
      <div className="nd-day-scroll-picker__weekdays">
        {WEEKDAY_SHORT.map(d => <span key={d}>{d}</span>)}
      </div>
      <div className="nd-day-scroll-picker__months" ref={scrollRef}>
        {scrollMonths.map((month, index) => {
          const monthKey = `${month.getFullYear()}-${month.getMonth()}`;
          return (
            <div
              key={monthKey}
              className="nd-day-scroll-picker__month"
              data-month-key={monthKey}
            >
              {index > 0 && <div className="nd-day-scroll-picker__separator" />}
              <button
                type="button"
                className="nd-day-scroll-picker__month-caption"
                onMouseDown={handleBackToYear}
                onClick={handleBackToYear}
              >
                {MONTHS_ABBR[month.getMonth()]} {month.getFullYear()}
              </button>
              <DayPicker
                month={month}
                canChangeMonth={false}
                onDayClick={onDayClick}
                selectedDays={selectedDate || undefined}
                modifiers={{
                  defaultToday: day => !isSelectedToday
                    && day.getFullYear() === today.getFullYear()
                    && day.getMonth() === today.getMonth()
                    && day.getDate() === today.getDate(),
                }}
                captionElement={() => null}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

ScrollableMonthPicker.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  anchorMonth: PropTypes.instanceOf(Date).isRequired,
  onDayClick: PropTypes.func.isRequired,
  onBackToYear: PropTypes.func.isRequired,
};

ScrollableMonthPicker.defaultProps = {
  selectedDate: null,
};

/* Dates are consumed and used in a variety of ways in this component. */
const formatForDisplay = date => moment(date).format('M/D/YYYY');
const formatForOutput = date => (date ? moment(date).format('YYYY-MM-DD') : '');

const formatForInput = date => (date ? moment(date).toDate() : null);
const INPUT_DATE_FORMATS = ['M/D/YYYY', 'MM/D/YYYY', 'M/DD/YYYY', 'MM/DD/YYYY'];
const formatForValidity = date => moment(date, INPUT_DATE_FORMATS, true);
const formatForParsing = date =>
  (formatForValidity(date).isValid()
    ? formatForValidity(date).toDate()
    : undefined);
const isValidDateObject = date => date instanceof Date && !Number.isNaN(date.getTime());
const isDateValueValid = date => date === null || isValidDateObject(date);

const defaultErrorText = 'Date format should be MM/DD/YYYY';

const Datepicker = ({
  disabled,
  onDateChange,
  onDayPickerHide,
  onBlur,
  onFocus,
  date: origDate,
  updateValidity,
  scrollOnFocus,
  requiredText,
  isMagnified,
  isHighlighted,
}) => {
  const parsedOrig = formatForInput(origDate);
  const parsedOrigIsValid = isDateValueValid(parsedOrig);
  const [currDate, setCurrDate] = useState(parsedOrigIsValid ? parsedOrig : null);
  const [inputValue, setInputValue] = useState(parsedOrigIsValid && parsedOrig ? formatForDisplay(parsedOrig) : '');
  const [isCurrDateValid, setIsCurrDateValid] = useState(parsedOrigIsValid);
  const [isEditing, setIsEditing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [calendarView, setCalendarView] = useState('yearMonth'); // 'yearMonth' | 'day'
  const [displayMonth, setDisplayMonth] = useState(parsedOrigIsValid && parsedOrig ? parsedOrig : new Date());

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const ignoreNextOutsideClickRef = useRef(false);
  const didNotifyHideRef = useRef(false);

  // Sync when origDate prop changes
  useEffect(() => {
    const parsed = formatForInput(origDate);
    const valid = isDateValueValid(parsed);
    setCurrDate(valid ? parsed : null);
    setInputValue(valid && parsed ? formatForDisplay(parsed) : '');
    setIsCurrDateValid(valid);
    updateValidity(valid);
    if (valid && parsed) {
      setDisplayMonth(parsed);
    } else {
      setDisplayMonth(new Date());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origDate]);

  // Close overlay when clicking outside the datepicker container
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutside = (e) => {
      if (ignoreNextOutsideClickRef.current) {
        ignoreNextOutsideClickRef.current = false;
        return;
      }
      // Guard: when a React state update removes e.target from the DOM before this
      // listener fires (e.g. switching calendar view), contains() returns false on
      // the detached node and would wrongly close the overlay. Skip those cases.
      if (!document.documentElement.contains(e.target)) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeOverlay(); // eslint-disable-line no-use-before-define
      }
    };
    document.addEventListener('click', handleOutside);
    return () => document.removeEventListener('click', handleOutside);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeOverlay = () => {
    setIsOpen(false);
    setIsEditing(false);

    const shouldSkipHideNotification = didNotifyHideRef.current;
    if (shouldSkipHideNotification) {
      didNotifyHideRef.current = false;
    } else {
      const outCurr = formatForOutput(currDate);
      const outOrig = formatForOutput(formatForInput(origDate));
      if (outCurr !== outOrig) {
        onDayPickerHide(outCurr);
        didNotifyHideRef.current = true;
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Escape' || !isOpen) return;
    e.preventDefault();
    e.stopPropagation();
    closeOverlay();
  };

  const openOverlay = () => {
    if (isOpen) return;
    didNotifyHideRef.current = false;
    setIsOpen(true);
    setCalendarView('yearMonth'); // always open in year/month view — matches md-current-view="year"
    setDisplayMonth(currDate || new Date());
    setIsEditing(true);
    if (scrollOnFocus) {
      setTimeout(() => {
        if (containerRef.current) containerRef.current.scrollIntoView();
      });
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (val === '') {
      setCurrDate(null);
      setDisplayMonth(new Date());
      onDateChange('');
      setIsCurrDateValid(true);
      updateValidity(true);
      didNotifyHideRef.current = false;
      return;
    }
    const parsed = formatForParsing(val);
    if (parsed) {
      const outParsed = formatForOutput(parsed);

      setCurrDate(parsed);
      onDateChange(outParsed);
      didNotifyHideRef.current = false;
      setDisplayMonth(parsed);
      setIsCurrDateValid(true);
      updateValidity(true);
    } else {
      didNotifyHideRef.current = false;
      setIsCurrDateValid(false);
      updateValidity(false);
    }
  };

  const handleInputFocus = () => {
    onFocus();
  };

  const handleInputKeyDown = (e) => {
    if (disabled) return;
    if (e.altKey && e.key === 'ArrowDown') {
      e.preventDefault();
      openOverlay();
    }
  };

  const handleBlur = (e) => {
    // Suppress blur if focus moved to an element inside our overlay (e.g. month button)
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) return;

    if (didNotifyHideRef.current) {
      didNotifyHideRef.current = false;
      onBlur(e);
      return;
    }

    if (isCurrDateValid) {
      const outCurr = formatForOutput(currDate);
      const outOrig = formatForOutput(formatForInput(origDate));
      if (outCurr !== outOrig) {
        onDayPickerHide(outCurr);
        didNotifyHideRef.current = true;
      }
    }

    onBlur(e);
  };

  const handleDayClick = (day, modifiers) => {
    if (modifiers && modifiers.disabled) return;

    const outDay = formatForOutput(day);
    const outOrig = formatForOutput(formatForInput(origDate));

    onDateChange(outDay);
    if (outDay !== outOrig) {
      onDayPickerHide(outDay);
    }
    didNotifyHideRef.current = true;
    setCurrDate(day);
    setInputValue(formatForDisplay(day));
    setIsCurrDateValid(true);
    updateValidity(true);
    setIsOpen(false);
    setIsEditing(false);
  };

  const handleMonthSelect = (year, month) => {
    setDisplayMonth(new Date(year, month));
    setCalendarView('day');
  };

  // Formatting
  const getErrorMessage = () => {
    if (!isCurrDateValid && !isEditing) {
      return <p className="error-message">{defaultErrorText}</p>;
    } else if (!origDate && requiredText) {
      return <p className="error-message">{requiredText}</p>;
    }
    return '';
  };

  const hasError = (!isCurrDateValid && !isEditing) || Boolean(!origDate && requiredText);
  const isEmpty = !currDate;
  const containerClass = [
    'DayPickerInput',
    isMagnified ? 'DayPickerInput--magnified' : '',
    isHighlighted ? 'DayPickerInput--highlighted' : '',
    hasError ? 'invalid-input' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`nd-datepicker${isEmpty ? ' nd-datepicker--empty' : ''}`} ref={containerRef} onKeyDown={handleKeyDown}>
      <div className={containerClass}>
        <input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder="MM/DD/YYYY"
        />
      </div>
      <Icon
        onClick={() => {
          if (disabled) return;
          if (inputRef.current) inputRef.current.focus();
          openOverlay();
        }}
        iconName="calendar"
      />
      {isOpen && (
        <div className="DayPickerInput-OverlayWrapper">
          <div className="DayPickerInput-Overlay">
            {calendarView === 'yearMonth' ? (
              <YearMonthPicker
                selectedDate={currDate}
                onMonthSelect={handleMonthSelect}
                anchorYear={displayMonth.getFullYear()}
              />
            ) : (
              <ScrollableMonthPicker
                selectedDate={currDate}
                anchorMonth={displayMonth}
                onDayClick={handleDayClick}
                onBackToYear={() => {
                  ignoreNextOutsideClickRef.current = true;
                  setCalendarView('yearMonth');
                }}
              />
            )}
          </div>
        </div>
      )}
      {getErrorMessage()}
    </div>
  );
};

export default Datepicker;

Datepicker.propTypes = {
  disabled: PropTypes.bool,
  onDateChange: PropTypes.func.isRequired,
  onDayPickerHide: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  date: PropTypes.string,
  updateValidity: PropTypes.func,
  scrollOnFocus: PropTypes.bool,
  requiredText: PropTypes.string,
  isHighlighted: PropTypes.bool,
  isMagnified: PropTypes.bool,
};

Datepicker.defaultProps = {
  date: '',
  disabled: false,
  onBlur: () => {},
  onFocus: () => {},
  scrollOnFocus: false,
  updateValidity: () => {},
  onDayPickerHide: () => {},
  requiredText: '',
  isMagnified: false,
  isHighlighted: false,
};
